import { Button, Card } from "@pushpress/pushpress-ui";
import AccountSubscriptionDetails from "./AccountSubscriptionDetails";
import { useCreateBillingPortalSession } from "../../../apis/billingApi.hooks";
import { toast } from "react-toastify";
import { defaultToastOptions } from "../../../utils/misc";
import { useState } from "react";
import AccountCancellationModal from "./AccountCancellationModal";
import {
  allStripeSubscriptionInfo,
  getSubscriptionInfo,
} from "../constants/subscriptionInfoMap";
import { CustomerStripeSubscriptionSummary } from "../../../apis/billingApi";
import { useAnalytics } from "../../../components/providers/AnalyticsProvider";
import {
  CORE_STRIPE_PRICE_IDS,
  STRIPE_PRICE_IDS,
} from "../constants/subscriptionIds";
import { AccountEvent } from "../constants/account.events";

interface AccountInfoCardProps {
  subscriptionSummaries: CustomerStripeSubscriptionSummary[];
}

const AccountInfoCard: React.FC<AccountInfoCardProps> = ({
  subscriptionSummaries = [],
}) => {
  const { mutate: createPortalSession, isLoading } =
    useCreateBillingPortalSession();
  const [showCancelOrDowngradePlan, setShowCancelOrDowngradePlan] =
    useState(false);
  const { trackEvent } = useAnalytics();
  const returnUrl = window.location.href;

  const getCoreTierLevel = (priceId: string): number => {
    if (
      priceId === STRIPE_PRICE_IDS.coreMaxMonthly ||
      priceId === STRIPE_PRICE_IDS.coreMaxAnnual
    ) {
      return 3;
    }
    if (
      priceId === STRIPE_PRICE_IDS.coreProMonthly ||
      priceId === STRIPE_PRICE_IDS.coreProAnnual ||
      priceId === STRIPE_PRICE_IDS.coreAnnualDiscounted
    ) {
      return 2;
    }
    if (priceId === STRIPE_PRICE_IDS.coreFree) {
      return 1;
    }
    return 0;
  };

  const getFilteredSubscriptions = (
    subscriptions: CustomerStripeSubscriptionSummary[],
  ) => {
    const coreSubscriptions = subscriptions.filter((subscription) =>
      CORE_STRIPE_PRICE_IDS.includes(subscription.priceId),
    );
    const nonCoreSubscriptions = subscriptions.filter(
      (subscription) => !CORE_STRIPE_PRICE_IDS.includes(subscription.priceId),
    );

    // During subscription upgrades, users may temporarily have multiple
    // Core subscriptions until reconciliation occurs on the backend.
    // Show only the highest tier to avoid confusion.
    if (coreSubscriptions.length > 1) {
      const highestTierSubscription = coreSubscriptions.reduce(
        (highest, current) => {
          const currentTierLevel = getCoreTierLevel(current.priceId);
          const highestTierLevel = getCoreTierLevel(highest.priceId);
          return currentTierLevel > highestTierLevel ? current : highest;
        },
      );

      return [...nonCoreSubscriptions, highestTierSubscription];
    }

    return subscriptions;
  };

  const filteredSubscriptions = getFilteredSubscriptions(subscriptionSummaries);

  const subscriptionInfo = filteredSubscriptions
    .map((subscription) => {
      const subInfo = getSubscriptionInfo(
        allStripeSubscriptionInfo,
        subscription.priceId,
      );

      if (subInfo) {
        return {
          ...subInfo,
          status: subscription.status,
        };
      }
    })
    .filter(Boolean);

  return (
    <Card className="tw-max-w-[1050px] tw-min-w-[280px]">
      <div className="tw-flex tw-flex-col md:tw-flex-row tw-min-h-[180px] tw-p-5 tw-gap-4 tw-border-b tw-border-neutral-200">
        {subscriptionInfo.length > 0 ? (
          subscriptionInfo.map((info) => (
            <AccountSubscriptionDetails
              key={`acccount-info-card-item-${info.title}`}
              title={info.title}
              description={info.description}
              status={info.status}
            />
          ))
        ) : (
          <div className="tw-text-neutral-500">No active subscriptions</div>
        )}
      </div>
      <div className="tw-bg-neutral-50 tw-p-4 tw-flex tw-flex-col sm:tw-flex-row tw-gap-3 sm:tw-gap-5">
        <Button
          variant="outline"
          onClick={() => {
            trackEvent(AccountEvent.ViewBillingPortalClicked, {
              location: "Account Page",
            });
            createPortalSession(returnUrl, {
              onSuccess: (data) => {
                if (data.url) {
                  window.open(data.url, "_blank");
                } else if (data.message) {
                  toast.error(data.message, {
                    ...defaultToastOptions,
                  });
                }
              },
              onError: () => {
                trackEvent("Failed to open Stripe Customer Portal", {
                  location: "Account Page",
                });
                toast.error("Failed to open Stripe Customer Portal", {
                  ...defaultToastOptions,
                });
              },
            });
          }}
          isSubmitting={isLoading}
        >
          Payment method & invoices
        </Button>
        <Button
          variant="outline"
          size="default"
          onClick={() => {
            trackEvent(AccountEvent.CancelOrDowngradePlanClicked, {
              location: "Account Page",
            });
            setShowCancelOrDowngradePlan(true);
          }}
        >
          Cancel or downgrade plan
        </Button>
        <AccountCancellationModal
          showCancelOrDowngradePlan={showCancelOrDowngradePlan}
          setShowCancelOrDowngradePlan={setShowCancelOrDowngradePlan}
          subscriptionSummaries={subscriptionSummaries}
        />
      </div>
    </Card>
  );
};

export default AccountInfoCard;
